/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';
import user from 'js/lib/user';

import { Button, Dialog, Typography2, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { logo, numberOfProductsNoPlusSign } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import CourseraLogo from 'bundles/page/components/CourseraLogo';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/subscriptions';

type PropsToComponent = {
  onClose: () => void;
  isOpen: boolean;
  subscription: SubscriptionsV1;
  s12nName?: string;
};

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const styles = {
  dialog: css`
    position: relative;

    .cds-Dialog-dialog {
      max-width: 700px !important;
    }
  `,
  action: css`
    padding-top: 16px;
  `,
  headingGroup: css`
    padding-bottom: var(--cds-spacing-300);
  `,
  listHead: css`
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: -0.1px;
  `,
  logo: css`
    margin-bottom: var(--cds-spacing-150);
    ${breakpoints.down('sm')} {
      margin-top: 2px;
    }
  `,
  courseraLogo: css`
    height: 16px;
  `,
  plusPill: css`
    margin-top: -2px;
  `,
};
const ReactivateSubscriptionConfirmationModal = ({ isOpen, onClose, subscription, s12nName }: PropsToComponent) => {
  const retrackedTrack = useRetracked();
  const handleClose = () => {
    onClose();
    retrackedTrack({
      trackingData: { subscription },
      trackingName: 'close_reactivate_subscription_confirmation_modal',
      action: 'click',
    });
  };
  const { Content, Actions } = Dialog;
  const userInfo = user.get();
  const isCourseraPlusSubscription = subscription.isCourseraPlusSubscription;

  const renderCourseraPlusConfirmationContent = () => {
    const dash = (
      <span>
        <strong>&#8212;</strong>
      </span>
    );
    return (
      <>
        <Typography2 component="h4" variant="titleSmall" css={styles.listHead}>
          {_t('With your Plus subscription you can:')}
        </Typography2>
        <ul>
          <li>
            <Typography2 component="p">
              <FormattedMessage
                message={_t('{findYourFit}{dash}try any course(s) with the {courseraPlus} badge')}
                findYourFit={<strong>{_t('Find your fit')}</strong>}
                dash={dash}
                courseraPlus={
                  <Imgix
                    css={styles.plusPill}
                    src={logo.PLUS_PILL_BLUE}
                    maxWidth={41}
                    maxHeight={16}
                    alt={_t('Coursera Plus')}
                  />
                }
              />
            </Typography2>
          </li>
          <li>
            <Typography2 component="p">
              <FormattedMessage
                message={_t('{getHandsOnExperience}{dash}learn job-ready skills with over 500 projects')}
                getHandsOnExperience={<strong>{_t('Get hands-on experience')}</strong>}
                dash={dash}
              />
            </Typography2>
          </li>
          <li>
            <Typography2 component="p">
              <FormattedMessage
                message={_t('{growYourCareer}{dash}skill up with access to over {numberOfProducts} learning programs')}
                growYourCareer={<strong>{_t('Grow your career')}</strong>}
                dash={dash}
                numberOfProducts={numberOfProductsNoPlusSign}
              />
            </Typography2>
          </li>
        </ul>
      </>
    );
  };
  const renderS12nConfirmationContent = () => {
    return (
      <Typography2 component="p">
        {_t(
          `Congratulations! You've taken a big step toward your goals. Your subscription to #{s12nName} is now active.`,
          { s12nName }
        )}
      </Typography2>
    );
  };

  const renderTitle = () => {
    if (isCourseraPlusSubscription) {
      return (
        <div css={styles.headingGroup}>
          <Imgix src={logo.PLUS_FULL_BLUE} alt={_t('Coursera Plus logo')} maxWidth={150} css={styles.logo} />
          <Typography2 component="h2" variant="titleSmall">
            {_t(`Welcome back to Coursera Plus 🎉`)}
          </Typography2>
        </div>
      );
    }

    return (
      <div css={styles.headingGroup}>
        <CourseraLogo css={[styles.logo, styles.courseraLogo]} />
        <Typography2 component="h2" variant="titleSmall">
          {_t(`You're all set, #{learnerName} 🎉`, { learnerName: userInfo.full_name })}
        </Typography2>
      </div>
    );
  };

  return (
    <Dialog
      aria-describedby="dialog-content"
      open={isOpen}
      variant="standard"
      width="medium"
      onClose={handleClose}
      css={styles.dialog}
      data-testid="reactivate-subscription-confirmation-modal"
    >
      <Content>
        {renderTitle()}
        <TrackedDiv
          withVisibilityTracking={true}
          trackingName="reactivate_subscription_confirmation_modal"
          trackingData={{ subscriptionId: subscription.id, userId: userInfo.id }}
        >
          {isCourseraPlusSubscription ? renderCourseraPlusConfirmationContent() : renderS12nConfirmationContent()}
        </TrackedDiv>
      </Content>
      <Actions css={styles.action}>
        <TrackedButton
          trackingName="reactivate_subscription_confirmation_modal_continue_button"
          variant="primary"
          size="medium"
          onClick={handleClose}
        >
          {_t('Got it')}
        </TrackedButton>
      </Actions>
    </Dialog>
  );
};

export default ReactivateSubscriptionConfirmationModal;
